import {
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from "@mui/material";
import React, {forwardRef} from "react";
import { FileInputAdornmentBasic } from "src/components/file-input";
import { ArrayInput, AutocompleteInput, WysiwygInput } from "src/components/input";
import { MuiColorInput } from 'mui-color-input'

export const TypeField = forwardRef((props: any, ref) => {
    const {data, type, required, defaultValue, name, label, options, additional, onChange, ...rest} = props;
  
    let componentField = null;

    switch (type) {
      case "select": {
        componentField = (
          <FormControl fullWidth>
            <InputLabel>{label}</InputLabel>
            <Select
              id={name}
              value={data?.[name] ?? defaultValue}
              size="small"
              onChange={(e) =>
                onChange(e.target.value, name)
              }
              {...rest}
            >
              {options.map((option: any, optionIndex: number) => (
                <MenuItem
                  key={optionIndex}
                  value={option.value}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
        break;
      }
      case "multiselect": {
        componentField = (
          <FormControl fullWidth>
            <InputLabel>{label}</InputLabel>
            <Select
              multiple
              id={name}
              value={data?.[name] && !Array.isArray(data?.[name]) ? data?.[name].split(",") : data?.[name] ?? []}
              label={label}
              size="small"
              onChange={(e) =>
                onChange(e.target.value, name)
              }
              sx={{
                '& .MuiSelect-multiple': {
                  whiteSpace: 'normal !important',
                },
              }}
              {...rest}
            >
              {options.map((option: any, optionIndex: number) => (
                <MenuItem
                  key={optionIndex}
                  value={option.value}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
        break;
      }
      case "wysiwyg": {
        componentField = (
          <WysiwygInput 
            required={required}
            fullWidth 
            label={label ?? name}
            key={name}
            name={name}
            value={data?.[name] ?? defaultValue}
            onChange={(value) =>
              onChange(value, name)
            }
            {...rest}       
          />
        );
        break;
      }
      case "multiline": {
        componentField = (
          <TextField
            label={label ?? name}
            key={name}
            name={name}
            value={data?.[name] ?? defaultValue}
            required={required}
            size="small"
            multiline
            rows={4}
            onChange={(e) =>
              onChange(e.target.value, name)
            }
            {...rest}
          ></TextField>
        );
        break;
      }
      case "color": {
        componentField = (
          <MuiColorInput
            format="hex"
            label={label ?? name}
            key={name}
            name={name}
            value={data?.[name] ?? defaultValue}
            required={required}
            size="small"
            onChange={(value) =>
              onChange(value, name)
            }
            {...rest}
          ></MuiColorInput>
        );
        break;
      }
      case "file": {
        componentField = (
          <FileInputAdornmentBasic
              label={label ?? name}
              key={name}
              name={name}
              value={data?.[name] ?? defaultValue}
              required={required}
              path="admin/media/common"
              fileTypes={["JPG", "JPEG", "PNG", "GIF", "WEBP", "PDF", "XLSX", "XLS", "DOCX"]}
              multiple={false}
              includeDomain={true}
              onChange={onChange}
              {...rest}
          />
        );
        break;
      }
      case "image": {
        componentField = (
          <FileInputAdornmentBasic
              label={label ?? name}
              key={name}
              name={name}
              value={data?.[name] ?? defaultValue}
              required={required}
              fileTypes={["JPG", "JPEG", "PNG", "GIF", "WEBP", "PDF", "XLS", "XLSX", "DOCX"]}
              multiple={false}
              includeDomain={true}
              onChange={onChange}
              {...rest}
          />
        );
        break;
      }
      case "product_slug": {
        componentField = (
          <AutocompleteInput
            resource="admin/products"
            label={label ?? name}
            name={name}
            value={data?.[name] ?? defaultValue}
            required={required}
            onChange={onChange}
            property="slug"
            getOptionLabel={(option) => option.sku && option.name ? `${option.sku} - ${option.name}` : ''}
            {...rest}
          />
        );
        break;
      }
      case "products": {
        componentField = (
          <AutocompleteInput
            resource="admin/products"
            multiple
            label={label ?? name}
            name={name}
            value={data?.[name] ? data?.[name].split(',')  : []}
            required={required}
            onChange={onChange}
            property="id"
            getOptionLabel={(option) => option.sku && option.name ? `${option.sku} - ${option.name}` : ''}
            {...rest}
          />
        );
        break;
      }
      case "product": {
        componentField = (
          <AutocompleteInput
            resource="admin/products"
            label={label ?? name}
            name={name}
            value={data?.[name] ?? defaultValue}
            required={required}
            onChange={onChange}
            property="id"
            getOptionLabel={(option) => option.sku && option.name ? `${option.sku} - ${option.name}` : ''}
            {...rest}
          />
        );
        break;
      }
      case "category": {
        componentField = (
          <AutocompleteInput
            resource="admin/product/categories"
            label={label ?? name}
            name={name}
            value={data?.[name] ?? defaultValue}
            required={required}
            onChange={onChange}
            property="id"
            getOptionLabel={(option) => option.name || ''}
            {...rest}
          />
        );
        break;
      }
      case "faqCategory": {
        componentField = (
          <AutocompleteInput
            resource="admin/faq/categories"
            label={label ?? name}
            name={name}
            value={data?.[name] ?? defaultValue}
            required={required}
            onChange={onChange}
            property="id"
            getOptionLabel={(option) => option.name || ''}
            {...rest}
          />
        );
        break;
      }
      case "array": {
        componentField = (
          <ArrayInput 
            label={label ?? name}
            key={name}
            name={name}
            value={data?.[name] ?? []}
            onChange={onChange}
            {...rest}
          >
              {additional.map((field, index) => {
                return (
                  <TypeField 
                    key={index}
                    type={field?.type} 
                    required={field?.required ?? false}
                    name={field.name} 
                    label={field.label}
                    onChange={onChange}
                  />
                );
              })}
          </ArrayInput>
        );
        break;
      }
      default: {
        componentField = (
          <TextField
            label={label ?? name}
            key={name}
            name={name}
            value={data?.[name] ?? defaultValue}
            required={required}
            size="small"
            onChange={(e) =>
              onChange(e.target.value, name)
            }
            {...rest}
          ></TextField>
        );
        break;
      }
    }
  
    return componentField;
});